import React from 'react'
import get from 'lodash/get'
import sortBy from 'lodash/sortBy'
import { RichText } from 'prismic-reactjs'
import Carousel from '../Carousel'
import VideoPlayer from '../VideoPlayer'
import ProjectInfo from '../ProjectInfo'
import styled from 'styled-components'
import {
  isVimeoFileUrl,
  normalizeVimeoFileUrl,
  vimeoIdFromLink,
} from '../../utils/helpers'

import { prismicImg } from '../../utils/prismicImage'
const StyledGrid = styled.div``

function Grid({ clients = [], onResize = () => {}, playerTheme = 'blue' }) {
  // Match ClientList sorting exactly
  const getProjectTitleKey = (p) => {
    const title = RichText.asText(get(p, 'primary.project_title', [])) || ''
    return title.trim().toLowerCase()
  }

  return (
    <StyledGrid className="grid-wrapper">
      {clients.map((client, clientIndex) => {
        // ✅ Sort projects A–Z under each client (same as ClientList)
        const projects = sortBy(get(client, 'data.body', []), getProjectTitleKey)

        return projects.map((p, projectIndex) => {
          const type = get(p, 'slice_type', '')
          const wayfinderId = `${get(
            client,
            'data.category',
            'project',
          )}-${clientIndex}-${projectIndex}`

          if (type === 'video') {
            const videoLinkUrl = get(p, 'primary.video_link.url', '')
            const videoFileUrl = normalizeVimeoFileUrl(get(p, 'primary.video_file.url', ''))
            const mobileVideoUrl = normalizeVimeoFileUrl(
              get(p, 'primary.mobile_video_link.url', ''),
            )
            const videoUrl = videoFileUrl || (isVimeoFileUrl(videoLinkUrl) ? videoLinkUrl : '')
            const videoId = vimeoIdFromLink(videoLinkUrl || videoFileUrl)
            const imagePoster = get(p, 'primary.image_poster')
            const posterVideoUrl =
              normalizeVimeoFileUrl(get(p, 'primary.poster_video.url', '')) ||
              normalizeVimeoFileUrl(get(p, 'primary.looping_poster_video.url', '')) ||
              normalizeVimeoFileUrl(get(p, 'primary.video_poster.url', ''))

            return (
              <div
                className={`grid-item grid-${type}`}
                id={wayfinderId}
                key={wayfinderId}
              >
                <div className="content">
                  <VideoPlayer
                    videoId={videoId}
                    videoUrl={videoUrl}
                    mobileVideoUrl={mobileVideoUrl}
                    posterVideoUrl={posterVideoUrl}
                    playerTheme={playerTheme}
                    poster={imagePoster}
                  />
                  <ProjectInfo client={client} project={p} onResize={onResize} />
                </div>
              </div>
            )
          }

          if (type === 'looping_video') {
            const videoLinkUrl = get(p, 'primary.video_link.url', '')
            const videoFileUrl = normalizeVimeoFileUrl(get(p, 'primary.video_file.url', ''))
            const videoUrl = videoFileUrl || (isVimeoFileUrl(videoLinkUrl) ? videoLinkUrl : '')
            const videoId = vimeoIdFromLink(videoLinkUrl || videoFileUrl)
            const imagePoster = get(p, 'primary.image_poster')
            return (
              <div
                className={`grid-item grid-${type}`}
                id={wayfinderId}
                key={wayfinderId}
              >
                <div className="content">
                  <VideoPlayer
                    videoId={videoId}
                    videoUrl={videoUrl}
                    loop={true}
                    poster={imagePoster}
                  />
                  <ProjectInfo client={client} project={p} onResize={onResize} />
                </div>
              </div>
            )
          }

          if (type === 'multiple_images') {
            // supports mixed carousel slides (Prismic-hosted images OR mp4 files)
            const slides = get(p, 'items', [])
              .map((item) => {
                const videoUrl = normalizeVimeoFileUrl(get(item, 'video_file.url', ''))
                const vimeoUrl = get(item, 'video_link.url', '')
                const image = get(item, 'image')
                const imageUrl = get(image, 'url', '')
                const vimeoLoop = !!get(item, 'vimeo_loop', false)

                const directVideoUrl =
                  videoUrl || (isVimeoFileUrl(vimeoUrl) ? vimeoUrl : '')

                // Priority: MP4 > Vimeo > image
                if (directVideoUrl)
                  return {
                    type: 'video',
                    url: directVideoUrl,
                    vimeoId: vimeoIdFromLink(vimeoUrl || videoUrl),
                    loop: vimeoLoop,
                    poster: image,
                  }

                if (vimeoUrl)
                  return {
                    type: 'vimeo',
                    url: vimeoUrl,
                    loop: vimeoLoop,
                    poster: image,
                    alt: get(image, 'alt', ''),
                  }

                if (imageUrl)
                  return {
                    type: 'image',
                    url: prismicImg.carousel(imageUrl),
                    alt: get(item, 'image.alt', ''),
                  }
                return null
              })
              .filter(Boolean)

            return (
              <div
                className={`grid-item grid-${type}`}
                id={wayfinderId}
                key={wayfinderId}
              >
                <div className="content">
                  <Carousel slides={slides} onResize={onResize} playerTheme={playerTheme} />
                  <ProjectInfo client={client} project={p} onResize={onResize} />
                </div>
              </div>
            )
          }

          // default: single image (and any other slice types)
          return (
            <div
              className={`grid-item grid-${type}`}
              id={wayfinderId}
              key={wayfinderId}
            >
              <div className="content">
                <img
                  src={prismicImg.grid(get(p, 'primary.image.url', ''))}
                  alt={get(p, 'primary.image.alt', '')}
                  loading={projectIndex === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                />
                <ProjectInfo client={client} project={p} onResize={onResize} />
              </div>
            </div>
          )
        })
      })}
    </StyledGrid>
  )
}

export default Grid
